import { NgFor, NgForOf, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { NgbNav, NgbNavChangeEvent, NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { DropDownButtonModule } from '@progress/kendo-angular-buttons';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { TabStripModule } from '@progress/kendo-angular-layout';
import { Position, TooltipsModule } from '@progress/kendo-angular-tooltip';
import { moreVerticalIcon } from '@progress/kendo-svg-icons';
import { QPointButtonComponent } from '@qpoint/buttons';
import { QPointIconModule } from '@qpoint/icons';
import { injectDestroyRef } from '@qpoint/utils';
import { fromEvent, Subscription } from 'rxjs';

import { QPointTabNavItemComponent } from './tab-nav-item.component';
import { QPointTabNavMenuItemDirective } from './tab-nav-menu-item.directive';

@Component({
    selector: 'qpoint-tab-nav',
    templateUrl: './tab-nav.component.html',
    styleUrls: ['./tab-nav.component.scss'],
    imports: [TabStripModule, NgbNavModule, QPointIconModule, NgIf, NgFor, NgForOf, NgTemplateOutlet, QPointButtonComponent, InputsModule, TooltipsModule, ReactiveFormsModule, DropDownButtonModule]
})
export class QPointTabNavComponent {
  protected readonly moreVerticalIcon = moreVerticalIcon;
  private destroyRef = injectDestroyRef();
  private _activeTabId = signal<string | number | undefined>(undefined);
  tabNameInputElement: HTMLInputElement | undefined = undefined;
  activeTabId = this._activeTabId.asReadonly();
  tabNavItemInvalid = signal(false);

  private clickOutsideSubscription: Subscription | undefined = undefined;

  @Input()
  public tabOrientation: 'horizontal' | 'vertical' = 'horizontal';
  @Input()
  public menuCollapsed = false;

  @Input() set activeId(value: string | number | undefined) {
    this._activeTabId.set(value);
  }

  @Input()
  public destroyOnHide: boolean = true;

  @Input() canAddTab = false;

  @Input() errorPopupPosition: Position = 'right';

  @ContentChildren(QPointTabNavItemComponent)
  public tabNavItems: QueryList<QPointTabNavItemComponent>;

  @Output()
  navChange = new EventEmitter<NgbNavChangeEvent>();

  @Output() addTabClick = new EventEmitter();

  @ViewChild('ngbNavReference')
  private tabset: NgbNav;

  @ViewChild('tabNameInput') set tabNameInput(el: ElementRef<HTMLInputElement>) {
    this.tabNameInputElement = el?.nativeElement ?? undefined;
    this.tabNavItemInvalid.set(false);
    if (this.clickOutsideSubscription) {
      this.clickOutsideSubscription.unsubscribe();
    }
    if (!el) {
      return;
    }
    el.nativeElement.focus();

    el.nativeElement.oninput = () => {
      this.tabNavItemInvalid.set(el.nativeElement.value === '');
    };

    this.clickOutsideSubscription = fromEvent(document, 'pointerdown')
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: PointerEvent) => {
      if (!el.nativeElement.contains(event.target as Node)) {
        const activeEditTab = this.tabNavItems.find((tab) => tab.editMode());

        if (activeEditTab) {
          if(!this.tabNavItemInvalid()) {
            activeEditTab.handleTabNameChange(el.nativeElement.value);
          }
          activeEditTab.cancel();
        }
      }
    });
  }

  public toggleTabOrientation() {
    if (this.tabOrientation === 'horizontal') {
      this.tabOrientation = 'vertical';
    } else {
      this.menuCollapsed = false;
      this.tabOrientation = 'horizontal';
    }
  }

  public toggleMenuCollapsed() {
    if (this.tabOrientation === 'horizontal') {
      return;
    }
    this.menuCollapsed = !this.menuCollapsed;
  }

  toggleCanAddTab() {
    this.canAddTab = !this.canAddTab;
  }

  public onNavChange($event: NgbNavChangeEvent) {
    this.setActiveId($event.nextId);
    this.navChange.emit($event);
  }

  public select(tabId: string) {
    this.tabset.select(tabId);
  }

  setActiveId(id: string | number) {
    this._activeTabId.set(id !== '' ? id : undefined);
  }

  setTabEditMode(event: Event, tabNavItem: QPointTabNavItemComponent, state: boolean) {
    event.preventDefault();
    event.stopPropagation();

    tabNavItem.setEditMode(state);
  }

  confirmTabNameInput() {
    if(!this.tabNameInputElement) return;

    const activeEditTab = this.tabNavItems.find((tab) => tab.editMode());

    if (activeEditTab) {
      if(!this.tabNavItemInvalid()) {
        activeEditTab.handleTabNameChange(this.tabNameInputElement.value);
      }
      activeEditTab.cancel();
    }
  }

  onTabNameDoubleClick(tabNavItem: QPointTabNavItemComponent) {
    if(!tabNavItem.canEditName) return;

    tabNavItem.setEditMode(true);
  }

  handleTabNavMenuItemClick(tabNavItem: QPointTabNavItemComponent, event: QPointTabNavMenuItemDirective) {
    if(event.disabled) return;

    if(event.internalFunction) {
      switch(event.internalFunction) {
        case 'EDIT': tabNavItem.setEditMode(true); break;
      }
    } else {
      event.itemClicked.emit()
    }
  }
}
